import React, { useState } from 'react';

interface FeedbackForm {
  type: 'bug' | 'suggestion';
  title: string;
  description: string;
  steps: string;
  priority: 'low' | 'medium' | 'high';
  userAgent: string;
  timestamp: string;
}

export const BugReportsAndSuggestions: React.FC = () => {
  const [formData, setFormData] = useState<FeedbackForm>({
    type: 'bug',
    title: '',
    description: '',
    steps: '',
    priority: 'medium',
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString()
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Generate a GitHub issue URL with pre-filled content
      const issueTitle = `${formData.type === 'bug' ? '🐛 Bug Report' : '💡 Suggestion'}: ${formData.title}`;
      
      const issueBody = `
## ${formData.type === 'bug' ? 'Bug Report' : 'Feature Suggestion'}

**Priority:** ${formData.priority}
**Submitted:** ${new Date().toLocaleString()}

### Description
${formData.description}

${formData.type === 'bug' ? `### Steps to Reproduce
${formData.steps}

### Expected Behavior
Please describe what you expected to happen.

### Actual Behavior
Please describe what actually happened.` : `### Use Case
${formData.steps}

### Benefits
Please describe how this suggestion would improve the application.`}

### System Information
- **User Agent:** ${formData.userAgent}
- **Timestamp:** ${formData.timestamp}
- **Browser:** ${navigator.userAgent.includes('Chrome') ? 'Chrome' : navigator.userAgent.includes('Firefox') ? 'Firefox' : navigator.userAgent.includes('Safari') ? 'Safari' : 'Other'}
- **Platform:** ${navigator.platform}

---
*This report was submitted through the Traveller Ship Builder feedback form.*
      `.trim();

      // Create GitHub issue URL
      const githubUrl = `https://github.com/MedievalSteve/TravellerShipBuilderApp/issues/new?title=${encodeURIComponent(issueTitle)}&body=${encodeURIComponent(issueBody)}&labels=${formData.type === 'bug' ? 'bug' : 'enhancement'}`;
      
      // Open GitHub issue page in new tab
      window.open(githubUrl, '_blank');
      
      setSubmitStatus('success');
      
      // Reset form after successful submission
      setTimeout(() => {
        setFormData({
          type: 'bug',
          title: '',
          description: '',
          steps: '',
          priority: 'medium',
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        });
        setSubmitStatus('idle');
      }, 3000);

    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.title.trim().length >= 5 && formData.description.trim().length >= 10;

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            🐛 Bug Reports & 💡 Suggestions
          </h1>
          <p className="text-gray-600">
            Help improve the Traveller Ship Builder by reporting issues or sharing ideas
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Feedback Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type of Feedback
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="type"
                  value="bug"
                  checked={formData.type === 'bug'}
                  onChange={handleInputChange}
                  className="mr-2"
                />
                <span className="text-sm">🐛 Bug Report</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="type"
                  value="suggestion"
                  checked={formData.type === 'suggestion'}
                  onChange={handleInputChange}
                  className="mr-2"
                />
                <span className="text-sm">💡 Suggestion</span>
              </label>
            </div>
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Priority Level
            </label>
            <select
              name="priority"
              value={formData.priority}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="low">🟢 Low - Minor issue or nice-to-have feature</option>
              <option value="medium">🟡 Medium - Moderate impact or useful feature</option>
              <option value="high">🔴 High - Critical issue or important feature</option>
            </select>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder={formData.type === 'bug' ? 'Brief description of the bug' : 'Brief description of your suggestion'}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              minLength={5}
            />
            <p className="text-xs text-gray-500 mt-1">
              Minimum 5 characters
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Detailed Description *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder={formData.type === 'bug' ? 'Describe the bug in detail. What went wrong?' : 'Describe your suggestion in detail. What would you like to see?'}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              minLength={10}
            />
            <p className="text-xs text-gray-500 mt-1">
              Minimum 10 characters
            </p>
          </div>

          {/* Steps/Use Case */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {formData.type === 'bug' ? 'Steps to Reproduce' : 'Use Case / How it would work'}
            </label>
            <textarea
              name="steps"
              value={formData.steps}
              onChange={handleInputChange}
              placeholder={formData.type === 'bug' ? '1. Go to...\n2. Click on...\n3. See error...' : 'Describe how this feature would work or be used'}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-center">
            <button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              className={`px-8 py-3 rounded-lg font-medium transition-colors ${
                isFormValid && !isSubmitting
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? 'Submitting...' : `Submit ${formData.type === 'bug' ? 'Bug Report' : 'Suggestion'}`}
            </button>
          </div>

          {/* Status Messages */}
          {submitStatus === 'success' && (
            <div className="text-center p-4 bg-green-100 border border-green-400 text-green-700 rounded-md">
              ✅ Thank you! Your {formData.type} has been submitted successfully. 
              A new GitHub issue will open in your browser where you can review and submit it.
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="text-center p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
              ❌ There was an error submitting your feedback. Please try again.
            </div>
          )}
        </form>

        {/* Instructions */}
        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <h3 className="font-medium text-blue-800 mb-2">📋 How it works:</h3>
          <ol className="text-sm text-blue-700 space-y-1">
            <li>1. Fill out the form above with your feedback</li>
            <li>2. Click "Submit" to generate a GitHub issue</li>
            <li>3. A new tab will open with a pre-filled GitHub issue</li>
            <li>4. Review the issue and click "Submit new issue" on GitHub</li>
            <li>5. Your feedback will be tracked and managed through GitHub Issues</li>
          </ol>
        </div>

        {/* Contact Info */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>
            Your feedback helps make the Traveller Ship Builder better for everyone!
          </p>
          <p className="mt-1">
            All submissions are tracked through GitHub Issues for transparency and community involvement.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BugReportsAndSuggestions;
